/**
 * React Flow 自定义节点：ImageNode
 * 显示生成的图片 + prompt + 模型信息
 * 折叠/展开 + 复制完整提示词 + 删除前确认
 */

import { Handle, Position, useReactFlow, type NodeProps, type Node } from '@xyflow/react'
import { Download, ExternalLink, X } from 'lucide-react'
import { useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import { useConfirmStore } from './ConfirmDialog'
import { reflowChildren } from './canvas-layout'
import { REMIX_CARD_DEFAULT_DATA } from './RemixCardNode'

export interface ImageNodeData extends Record<string, unknown> {
  imageUrl: string
  prompt: string
  generationId: string
  model: string
  seed?: number
  collapsed?: boolean
}

export type ImageNodeType = Node<ImageNodeData, 'image-node'>

const BTN_BASE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.55)',
  border: '1px solid rgba(255,255,255,0.35)',
  color: '#fff',
  width: 24,
  height: 24,
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
}

const COPY_BTN: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid #334155',
  color: '#cbd5e1',
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: 10,
  cursor: 'pointer',
  flexShrink: 0,
}

export function ImageNode({ id, data, selected, parentId }: NodeProps<ImageNodeType>) {
  const { imageUrl, prompt, generationId, model, seed, collapsed = false } = data
  const rf = useReactFlow()
  const [copied, setCopied] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [downloading, setDownloading] = useState(false)

  const handleDelete = useCallback(() => {
    useConfirmStore.getState().show({
      title: '删除图片',
      message: '确认删除这张图片？',
      confirmText: '删除',
      danger: true,
      onConfirm: () => {
        rf.deleteElements({ nodes: [{ id }] })
        if (parentId) reflowChildren(rf, parentId)
      },
    })
  }, [rf, id, parentId])

  const handleToggle = useCallback(() => {
    rf.setNodes((nodes) =>
      nodes.map((n) =>
        n.id === id
          ? { ...n, data: { ...(n.data as ImageNodeData), collapsed: !((n.data as ImageNodeData).collapsed ?? false) } }
          : n,
      ),
    )
    if (parentId) reflowChildren(rf, parentId)
  }, [rf, id, parentId])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(prompt || '')
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch (e) {
      console.error('copy failed', e)
    }
  }, [prompt])

  const handleDownload = useCallback(async () => {
    if (!imageUrl || downloading) return
    setDownloading(true)
    try {
      const response = await fetch(imageUrl)
      if (!response.ok) throw new Error(`Download failed: ${response.status}`)
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = `${generationId || 'aurora-canvas-image'}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(objectUrl)
    } catch (e) {
      console.error('download failed', e)
      window.open(imageUrl, '_blank')
    } finally {
      setDownloading(false)
    }
  }, [downloading, generationId, imageUrl])

  const handleEdit = useCallback(() => {
    const allNodes = rf.getNodes()
    const self = allNodes.find((n) => n.id === id)
    if (!self) return

    let absX = self.position.x
    let absY = self.position.y
    if (parentId) {
      const parent = allNodes.find((n) => n.id === parentId)
      if (parent) {
        absX += parent.position.x
        absY += parent.position.y
      }
    }

    const newRemixId = `remix_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
    const newRemix = {
      id: newRemixId,
      type: 'prompt-remix',
      position: { x: absX + 420, y: absY },
      dragHandle: '.drag-handle',
      data: { ...REMIX_CARD_DEFAULT_DATA, prompt: prompt || '', model: model || REMIX_CARD_DEFAULT_DATA.model },
    }

    rf.setNodes((nodes) => [...nodes, newRemix])
    rf.setEdges((edges) => [
      ...edges,
      {
        id: `${id}->${newRemixId}`,
        source: id,
        target: newRemixId,
        animated: true,
        style: { stroke: '#06b6d4', strokeWidth: 2 },
      },
    ])
  }, [rf, id, parentId, prompt, model])

  return (
    <>
      <Handle type="target" position={Position.Left} style={{ background: '#6366f1', width: 10, height: 10 }} />
      <div
        style={{
          width: 360,
          background: '#0f172a',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: selected
            ? '0 0 0 2px #06b6d4, 0 4px 24px rgba(0,0,0,0.45)'
            : '0 4px 24px rgba(0,0,0,0.35)',
          fontFamily: 'Inter, system-ui, sans-serif',
          border: '1px solid #1e293b',
          position: 'relative',
        }}
      >
        <div
          className="nodrag"
          style={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 2,
            display: 'flex',
            gap: 6,
          }}
        >
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            onMouseDown={(e) => e.stopPropagation()}
            title="查看大图"
            style={BTN_BASE}
          >
            <ExternalLink size={13} />
          </button>
          <button
            type="button"
            onClick={handleDownload}
            onMouseDown={(e) => e.stopPropagation()}
            title="下载图片"
            style={BTN_BASE}
          >
            <Download size={13} />
          </button>
          <button
            type="button"
            onClick={handleToggle}
            onMouseDown={(e) => e.stopPropagation()}
            title={collapsed ? '展开' : '最小化'}
            style={BTN_BASE}
          >
            {collapsed ? '+' : '−'}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            onMouseDown={(e) => e.stopPropagation()}
            title="删除图片"
            style={BTN_BASE}
          >
            ✕
          </button>
        </div>

        {collapsed ? (
          <div
            className="drag-handle"
            style={{
              padding: '10px 80px 10px 12px',
              background: '#0b1120',
              color: '#cbd5e1',
              fontSize: 11,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              cursor: 'move',
              minHeight: 40,
            }}
          >
            <span style={{ fontSize: 14 }}>🖼️</span>
            <span
              title={prompt}
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                flex: 1,
              }}
            >
              {prompt || '—'}
            </span>
            <span style={{ color: '#64748b', flexShrink: 0, fontSize: 10 }}>
              {formatMeta(model, seed)}
            </span>
          </div>
        ) : (
          <>
            <div
              className="drag-handle"
              style={{
                background: '#020617',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                aspectRatio: '1 / 1',
                cursor: 'move',
              }}
              onDoubleClick={() => setPreviewOpen(true)}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={prompt}
                  draggable={false}
                  onClick={(e) => {
                    e.stopPropagation()
                    setPreviewOpen(true)
                  }}
                  style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', cursor: 'zoom-in' }}
                />
              ) : (
                <span style={{ color: '#475569', fontSize: 12 }}>暂无图片</span>
              )}
            </div>
            <div
              style={{
                padding: '8px 12px',
                background: '#0b1120',
                color: '#94a3b8',
                fontSize: 10,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderTop: '1px solid #1e293b',
                gap: 8,
              }}
            >
              <span
                title={prompt}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1,
                }}
              >
                {prompt || '—'}
              </span>
              <button
                type="button"
                className="nodrag"
                onClick={handleCopy}
                onMouseDown={(e) => e.stopPropagation()}
                title="复制完整提示词"
                style={COPY_BTN}
              >
                {copied ? '✓ 已复制' : '📋 复制'}
              </button>
              <button
                type="button"
                className="nodrag"
                onClick={handleDownload}
                onMouseDown={(e) => e.stopPropagation()}
                title="下载图片"
                style={COPY_BTN}
              >
                {downloading ? '下载中' : '下载'}
              </button>
              <button
                type="button"
                className="nodrag"
                onClick={handleEdit}
                onMouseDown={(e) => e.stopPropagation()}
                title="基于此图编辑（生成 Remix 卡片）"
                style={COPY_BTN}
              >
                ✏️ 编辑
              </button>
              <span style={{ color: '#64748b', flexShrink: 0 }}>{formatMeta(model, seed)}</span>
            </div>
          </>
        )}
      </div>
      <Handle type="source" position={Position.Right} style={{ background: '#8b5cf6', width: 10, height: 10 }} />
      {previewOpen
        ? createPortal(
            <ImagePreviewModal
              imageUrl={imageUrl}
              prompt={prompt}
              model={model}
              seed={seed}
              copied={copied}
              downloading={downloading}
              onClose={() => setPreviewOpen(false)}
              onCopy={handleCopy}
              onDownload={handleDownload}
              onEdit={handleEdit}
            />,
            document.body,
          )
        : null}
    </>
  )
}

function formatMeta(model: string, seed?: number): string {
  const modelLabel = model?.split('/').pop() || model || 'model'
  return seed !== undefined ? `${modelLabel} · seed ${seed}` : modelLabel
}

function ImagePreviewModal({
  imageUrl,
  prompt,
  model,
  seed,
  copied,
  downloading,
  onClose,
  onCopy,
  onDownload,
  onEdit,
}: {
  imageUrl: string
  prompt: string
  model: string
  seed?: number
  copied: boolean
  downloading: boolean
  onClose: () => void
  onCopy: () => void
  onDownload: () => void
  onEdit: () => void
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(2,6,23,0.88)',
        backdropFilter: 'blur(8px)',
        zIndex: 9998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(1120px, 96vw)',
          maxHeight: '92vh',
          background: '#0f172a',
          border: '1px solid #334155',
          borderRadius: 12,
          overflow: 'hidden',
          boxShadow: '0 24px 90px rgba(0,0,0,0.65)',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) 340px',
          color: '#e2e8f0',
          fontFamily: 'Inter, system-ui, sans-serif',
        }}
      >
        <div style={{ background: '#020617', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 520 }}>
          <img
            src={imageUrl}
            alt={prompt}
            style={{
              maxWidth: '100%',
              maxHeight: '92vh',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </div>
        <aside style={{ borderLeft: '1px solid #1e293b', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ padding: 14, borderBottom: '1px solid #1e293b', display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#f8fafc', fontSize: 14, fontWeight: 700 }}>生成结果</div>
              <div style={{ color: '#64748b', fontSize: 11, marginTop: 2 }}>{formatMeta(model, seed)}</div>
            </div>
            <button
              type="button"
              onClick={onClose}
              title="关闭"
              style={{
                background: 'transparent',
                border: '1px solid #334155',
                color: '#94a3b8',
                width: 30,
                height: 30,
                borderRadius: 6,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
              }}
            >
              <X size={15} />
            </button>
          </div>
          <div style={{ padding: 14, flex: 1, overflow: 'auto' }}>
            <div style={{ color: '#94a3b8', fontSize: 11, marginBottom: 6 }}>Prompt</div>
            <div
              style={{
                background: '#020617',
                border: '1px solid #1e293b',
                borderRadius: 8,
                padding: 10,
                color: '#cbd5e1',
                fontSize: 12,
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {prompt || '—'}
            </div>
          </div>
          <div style={{ padding: 14, borderTop: '1px solid #1e293b', display: 'flex', gap: 8 }}>
            <button type="button" onClick={onCopy} style={modalButtonStyle('secondary')}>
              {copied ? '已复制' : '复制 Prompt'}
            </button>
            <button type="button" onClick={onDownload} style={modalButtonStyle('secondary')}>
              {downloading ? '下载中' : '下载'}
            </button>
            <button type="button" onClick={onEdit} style={modalButtonStyle('primary')}>
              Remix
            </button>
          </div>
        </aside>
      </div>
    </div>
  )
}

function modalButtonStyle(kind: 'primary' | 'secondary'): React.CSSProperties {
  return {
    flex: 1,
    background: kind === 'primary' ? 'linear-gradient(90deg,#06b6d4,#6366f1)' : 'transparent',
    border: kind === 'primary' ? 'none' : '1px solid #334155',
    color: kind === 'primary' ? '#fff' : '#cbd5e1',
    padding: '9px 10px',
    borderRadius: 7,
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  }
}
